<template>
    <div class="page-root">
        <!-- Top bar -->
        <div>
            <div class="topbar-bg" :class="calcTopbarClass"></div>
        </div>

        <div class="text-content-section">

            <div v-if="isCalendar" class="calendar-wrapper">

                <v-calendar is-expanded class="full-calendar">
                    <template v-slot:day-content="{ day }">
                        <div class="calendar-day">

                            <!-- day number -->
                            <div class="day-header">
                                {{ day.day }}
                            </div>

                            <!-- events -->
                            <div class="event-list" v-if="calendarData[day.id]">
                                <div v-for="(event, index) in calendarData[day.id].slice(0, 3)" :key="index"
                                    class="event-pill">
                                    {{ event }}
                                </div>

                                <div v-if="calendarData[day.id].length > 3" class="more-events">
                                    +{{ calendarData[day.id].length - 3 }} more
                                </div>
                            </div>

                        </div>
                    </template>
                </v-calendar>

            </div>

            <div v-else v-html="pageContent"></div>

        </div>
    </div>
</template>

<style>
.page-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.text-content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.calendar-wrapper {
  flex: 1;
  display: flex;
  min-height: 0;
}

.full-calendar {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.vc-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.vc-weekdays {
  background: #fafafa;
  border-bottom: 1px solid #ededed;
  font-size: 12px;
  font-weight: 600;
}

.vc-weeks {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  height: 100%;
}

.vc-day {
  display: flex;
  align-items: stretch;
  border-right: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  min-height: 100px;
}

.vc-day:nth-child(7n) {
  border-right: none;
}

.vc-day:hover {
  background: #f7f9fc;
}

.vc-day.vc-day-disabled,
.vc-day.is-not-in-month {
  background: #fafafa;
  color: #c0c0c0;
}

.vc-day.is-today {
  background: #f3f7ff;
}

.calendar-day {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 6px;
  gap: 4px;
  overflow: hidden;
}

.day-header {
  font-size: 12px;
  font-weight: 600;
  color: #444;
}

.vc-day.is-today .day-header {
  background: #0d6efd;
  color: #fff;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.event-pill {
  background: #eef4ff;
  color: #0b5ed7;
  font-size: 11px;
  padding: 3px 6px;
  border-radius: 4px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.event-pill:hover {
  background: #e3edff;
}

.more-events {
  font-size: 10px;
  color: #999;
}

@media (min-height: 800px) {
  .vc-day {
    min-height: 120px;
  }
}

@media (min-height: 1000px) {
  .vc-day {
    min-height: 140px;
  }
}

@media (min-height: 1200px) {
  .vc-day {
    min-height: 160px;
  }
}

</style>

<script>
/* eslint-disable */
import axios from 'axios'

export default {
    data() {
        return {
            pageContent: '',
            rawMarkdown: '',
            routeRoot: '',
        }
    },

    beforeRouteEnter(to, from, next) {
        const routeRoot = to.path.split('/')[1].toLowerCase();

        axios.get('api/pagecontent/' + to.name).then(response => {
            if (response.status === 200) {
                return next(vm => vm.setData(null, response.data, routeRoot));
            }
            return next(vm => vm.setData('error', null, routeRoot));
        })
            .catch(error => {
                next(vm => vm.setData(error, null, routeRoot));
            });
    },

    computed: {
        calcTopbarClass() {
            if (this.routeRoot === 'home') return 'topbar-welcome-bg';
            if (this.routeRoot.includes('arrivals')) return 'topbar-arrivals-bg';
            if (this.routeRoot.includes('stay')) return 'topbar-alice-springs-bg';
            if (this.routeRoot.includes('departures')) return 'topbar-departures-bg';
            if (this.routeRoot.includes('forms')) return 'topbar-mail-bg';
            if (this.routeRoot.includes('contact')) return 'topbar-financial-bg';
            if (this.routeRoot === 'logistical') return 'topbar-logistical-bg';

            return 'topbar-tools-bg';
        },

        isCalendar() {
            return /##\s+\d{4}-\d{2}-\d{2}/.test(this.rawMarkdown);
        },

        calendarData() {
            const lines = this.rawMarkdown.split('\n');
            const grouped = {};
            let currentDate = null;

            lines.forEach(line => {
                const match = line.match(/^##\s+(\d{4}-\d{2}-\d{2})/);

                if (match) {
                    currentDate = match[1];
                    grouped[currentDate] = [];
                } else if (currentDate && line.trim().startsWith('-')) {
                    grouped[currentDate].push(line.replace('-', '').trim());
                }
            });

            return grouped;
        }
    },

    methods: {
        setData(err, page, routeRoot) {
            this.routeRoot = routeRoot;

            if (err) {
                this.$router.push({ name: 'home' });
                return;
            }

            this.rawMarkdown = page.Content || '';

            let html = marked(this.rawMarkdown);

            this.pageContent = html
                .replace(/a href=/g, "a target='_blank' href=")
                .replaceAll('="https://itsvesta.com/', '="./')
                .replaceAll('="https://www.itsvesta.com/', '="./')
                .replaceAll('="https://vestaapp-staging.azurewebsites.net/', '="./');
        }
    }
}
</script>